import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { Box, Fab, Stack, Tooltip } from '@mui/material';
import { Query, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router-dom';

import BeeEditor from 'domains/content/components/Bee/BeeEditor';
import BeeEditorTestSendDialog from 'domains/content/components/Bee/BeeEditorTestSendDialog';
import SaveRowDialog from 'domains/content/components/Bee/SaveRowDialog';
import useBeeAutoSave from 'domains/content/components/Bee/useBeeAutoSave';
import { extractDisplayConditions } from 'domains/content/components/Bee/utils/extractDisplayConditions';
import CopyGeneratorChat, { SaraCircleMark } from 'domains/content/components/CopyGeneratorChat';
import {
    ApplicationAccessType,
    BeeEmailEditorConfig,
    CommunicationType,
    ContentTypeForComparison,
    CreateTemplateInput,
    isTemplate as isTemplatePageResponse,
    Template,
    UpdateTemplateInput,
} from 'domains/content/types';
import Alert from 'domains/core/components/Alert';
import Button from 'domains/core/components/Button';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import LanguageTabBar, { DEFAULT_LANGUAGE_CODE } from 'domains/core/components/LanguageTabBar';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar/SuccessSnackbar';
import downloadFile, { downloadBlob } from 'helpers/downloadFile/downloadFile';
import useCreateSavedRow from 'hooks/mutations/useCreateSavedRow';
import useCreateTemplate from 'hooks/mutations/useCreateTemplate';
import useLinkSavedRowTemplateLink from 'hooks/mutations/useLinkSavedRowTemplateLink';
import useUpdateSavedRow from 'hooks/mutations/useUpdateSavedRow';
import useUpdateTemplate from 'hooks/mutations/useUpdateTemplate';
import QueryKeys from 'hooks/queries/keys';
import useBeeConfig from 'hooks/queries/useBeeConfig';
import useRowDisplayConditions from 'hooks/queries/useDisplayConditions';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useSavedRowTemplateLinks from 'hooks/queries/useSavedRowTemplateLinks';
import useTemplate from 'hooks/queries/useTemplate';
import useTemplateHTML from 'hooks/queries/useTemplateHTML';
import useTemplatePDF from 'hooks/queries/useTemplatePDF';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useGetAxiosInstance from 'hooks/useGetAxiosInstance';
import useQueryParams from 'hooks/useQueryParams';
import { ApiMethods, BeeConfigType, CuredApiPaths, URLPaths } from 'models/enums';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';
import theme from 'theme';
import { buildEmailBeeSavedRowsConfig } from 'utils/buildEmailBeeSavedRowsConfig';
import {
    buildSavedRowLinkLookupMap,
    DEFAULT_SAVED_ROW_CATEGORY_HANDLES,
    getBeeRowInstanceUuid,
    labelForSavedRowCategoryHandle,
    mergeTemplateSavedRowLinksIntoBeeJson,
    resolveSavedRowLibraryLink,
} from 'utils/savedEmailRows';

import EmailExportSnackbar from './EmailExportSnackbar';

import type { IPluginRow } from '@beefree.io/sdk/dist/types/bee';
import type { SaveRowDialogIntent } from 'domains/content/components/Bee/SaveRowDialog/SaveRowDialog';
import type { SavedRowCategoryOption } from 'utils/savedEmailRows';

const buttonCopy = {
    color: theme.palette.warning.content,
    backgroundColor: 'theme.palette.warning.background',
    marginLeft: theme.spacing(1),
};

const getNameHelperText = (isNameUndefinedOnTouch: boolean, isNameTooShort: boolean) => {
    if (isNameUndefinedOnTouch) {
        return 'Please enter an email name';
    }

    if (isNameTooShort) {
        return 'Email name must have at least 3 characters';
    }
};

/**
 * Beefree validates `contentDialog.saveRow` `resolve()` output. Returning only bare `{ id, name, category }`
 * can fail validation and leave the editor in “waiting” forever; merge metadata into the row object Bee passed in.
 * The builder also expects `resolve(row, { synced })` (see `utils/buildEmailBeeSavedRowsConfig` saveRow handler).
 */

const buildSavedRowResolvePayload = (
    row: IPluginRow,
    created: { id: string; name: string; category: string }
): Record<string, unknown> => {
    const rowObj = (row as unknown) as Record<string, unknown>;
    const existingMeta =
        rowObj.metadata && typeof rowObj.metadata === 'object' && !Array.isArray(rowObj.metadata)
            ? { ...(rowObj.metadata as Record<string, unknown>) }
            : {};
    const instanceUuid = (typeof rowObj.uuid === 'string' && rowObj.uuid.trim()) || getBeeRowInstanceUuid(row);
    return {
        ...rowObj,
        ...(instanceUuid ? { uuid: instanceUuid } : {}),
        // Bee’s Rows UI reads `IPluginRow.name`; metadata alone shows “No name provided” after save.
        name: created.name,
        metadata: {
            ...existingMeta,
            id: created.id,
            name: created.name,
            category: created.category,
            savedRowId: created.id,
            categoryHandle: created.category,
        },
    };
};

const ContentEmailBuilderPage = () => {
    const [name, setName] = useState('');
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [isTestSendDialogOpen, setIsTestSendDialogOpen] = useState(false);
    const [isCopyGeneratorChatOpen, setIsCopyGeneratorChatOpen] = useState(false);
    /**
     * Bee-specific states, hooks, and functions
     */
    const [beeJson, setBeeJson] = useState('');
    const [isNoRestart, setIsNoRestart] = useState(false);
    const [isSaveNameClicked, setIsSaveNameClicked] = useState(false);
    const [isSaveBeeJsonClicked, setIsSaveBeeJsonClicked] = useState(false);
    const [communicationType, setCommunicationType] = useState(CommunicationType.COMMERCIAL);
    const [applicationsAccess, setApplicationsAccess] = useState([ApplicationAccessType.OUTREACH]);
    const [status, setStatus] = useState(ContentStatus.DRAFT);

    const history = useHistory();

    const query = useQueryParams();
    const queryClient = useQueryClient();
    const id = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    // templateId is null when creating new content (id=default) — chat history is not loaded in that case
    const templateId = id && id !== 'default' ? Number(id) : null;
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrlParam = query.get('status');
    // Normalize status to lowercase for consistent comparison
    const statusFromUrl = statusFromUrlParam ? (statusFromUrlParam.toLowerCase() as ContentStatus) : null;

    const onSuccess = (data: any) => {
        queryClient.invalidateQueries({
            // When we update a template, the cached network calls will be stale for the updated templates, so we need to invalidate the cached GET requests.
            predicate: (query: Query) => query.queryKey[0] === 'template-id',
        });

        // Reset to prevent useBeeAutoSave from running onUpdate() on every name change
        setIsSaveNameClicked(false);

        // If "Save & Exit" was clicked, always redirect to content listing page
        if (isSaveBeeJsonClicked) {
            setIsSaveBeeJsonClicked(false); // Reset the flag
            const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
            history.push(contentPath);
            return;
        }

        // If creating or copying a template (and Save & Exit was not clicked), change the route to transition to "edit" mode.
        if (isCreate || isCopy) {
            setIsNoRestart(true);
            history.push(
                getContentCreatePath({ contentType: ContentTypeForComparison.EMAIL, id: data.id, parentFolderId })
            );
        }
    };

    const {
        error: saveError,
        isError: isSaveError,
        isLoading: isCreateLoading,
        mutate: saveTemplate,
    } = useCreateTemplate({
        onSuccess,
    });

    const {
        error: updateError,
        isError: isUpdateError,
        isLoading: isUpdateLoading,
        mutate: updateTemplate,
    } = useUpdateTemplate(id, {
        onSuccess,
    });

    const {
        data: templateQuery,
        error: templateError,
        isError: isTemplateError,
        isSuccess: isTemplateSuccess,
    } = useTemplate({ templateId: id });

    const isTemplate = isTemplateSuccess && isTemplatePageResponse(templateQuery);

    // If the isCopy flag is set to true, the content create page saves a new template
    const isCopy = query.get('isCopy') === 'true';
    const isCreate = !isCopy && !isTemplate;
    const isUpdate = !isCopy && isTemplate;
    const isLayout = !isCopy && isTemplate && templateQuery.isLayout;
    const isError = isTemplateError || isSaveError || isUpdateError;

    const isSaveClicked = isSaveNameClicked || isSaveBeeJsonClicked;
    const isNameUndefinedOnSave = !name && isSaveClicked;
    const isNameUndefinedOnTouch = !name && isNameTouched;
    const isNameTooShort = isNameTouched && name.length < 3;
    const isSaving = isCreateLoading || isUpdateLoading;

    const {
        aiBrandTools: hasAiBrandTools,
        contentDraft: hasContentDraft,
        mlTemplateBuilder: isMlEnabled,
        reusableRows,
    } = useFlags();

    // Multilingual state (flag-gated)
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_CODE);
    const [languages, setLanguages] = useState<string[]>([DEFAULT_LANGUAGE_CODE]);
    // Per-language beeJson strings (flushed from editor on language switch)
    const [beeJsonByLanguage, setBeeJsonByLanguage] = useState<Record<string, string>>({});
    // Parsed JSON to load into the running Bee editor instance on language switch (null = no pending load)
    const [contentToLoad, setContentToLoad] = useState<object | null>(null);
    // Tracks whether the Bee editor is in preview mode (reported back via onPreviewModeChange)
    const [isEditorInPreview, setIsEditorInPreview] = useState(false);
    const { canManageContent } = useAccessCentrePermissions();

    const { data: templateRowLinksResponse } = useSavedRowTemplateLinks({
        templateId,
        reactQueryOptions: {
            enabled: Boolean(reusableRows && templateId != null && canManageContent),
        },
    });

    /** Only when `reusableRows` is on — avoids using cached links after the flag turns off. */
    const savedRowLinksFromApi = useMemo(() => (reusableRows ? templateRowLinksResponse?.links : undefined), [
        reusableRows,
        templateRowLinksResponse?.links,
    ]);

    const templateBeeJson = useMemo(() => {
        if (!isTemplate) {
            return templateQuery;
        }
        try {
            const parsed = JSON.parse(templateQuery.beeJson) as object;
            const links = savedRowLinksFromApi;
            if (links?.length) {
                return mergeTemplateSavedRowLinksIntoBeeJson(parsed, links) as object;
            }
            return parsed;
        } catch {
            return {};
        }
    }, [isTemplate, templateQuery, savedRowLinksFromApi]);

    const [saveRowDialogOpen, setSaveRowDialogOpen] = useState(false);
    const [saveRowSuccess, setSaveRowSuccess] = useState<{ key: number; message: string } | null>(null);
    const [saveRowApiError, setSaveRowApiError] = useState<{ key: number; message: string } | null>(null);
    const pendingSaveRowRef = useRef<{
        resolve: (metadata: Record<string, unknown>) => void;
        reject: () => void;
        row: IPluginRow;
    } | null>(null);
    /** Bee row `uuid` → library row after a successful save on the canvas (metadata may lag until re-fetch). */
    const canvasSavedRowByRowUuidRef = useRef(
        new Map<string, { savedRowId: string; name: string; category: string }>()
    );
    const getRowsApiInstance = useGetAxiosInstance(ApiMethods.GET);
    const getRowsApiInstanceRef = useRef(getRowsApiInstance);
    getRowsApiInstanceRef.current = getRowsApiInstance;
    const { mutateAsync: createSavedRowAsync, isLoading: isSavingSavedRow } = useCreateSavedRow();
    const { mutateAsync: updateSavedRowAsync, isLoading: isUpdatingSavedRow } = useUpdateSavedRow();
    const { mutateAsync: linkSavedRowToTemplateAsync } = useLinkSavedRowTemplateLink();
    const isSavingSavedRowMutation = isSavingSavedRow || isUpdatingSavedRow;

    const promptSaveRow = useCallback(
        (row: IPluginRow) =>
            new Promise<Record<string, unknown>>((resolve, reject) => {
                pendingSaveRowRef.current = { resolve, reject, row };
                // Let Beefree enter “waiting mode” first, then paint our modal on the next task (see Content Dialog docs).
                window.setTimeout(() => {
                    flushSync(() => {
                        setSaveRowDialogOpen(true);
                    });
                }, 0);
            }),
        []
    );

    const fetchRowsForCategory = useCallback(
        async (handle: string) => {
            if (!reusableRows) {
                return [];
            }
            const api = await getRowsApiInstanceRef.current();
            const { data } = await api.get(`${CuredApiPaths.SAVED_ROWS}/categories/${encodeURIComponent(handle)}`);
            return (Array.isArray(data) ? data : []) as IPluginRow[];
        },
        [reusableRows]
    );

    const handleSavedRowDialogClose = useCallback(() => {
        if (isSavingSavedRowMutation) {
            return;
        }
        pendingSaveRowRef.current?.reject();
        pendingSaveRowRef.current = null;
        setSaveRowDialogOpen(false);
    }, [isSavingSavedRowMutation]);

    const handleSavedRowDialogSubmit = useCallback(
        async ({ name, category }: { name: string; category: string }, intent: SaveRowDialogIntent) => {
            const pending = pendingSaveRowRef.current;
            if (!pending) {
                // Bee is still waiting for resolve/reject; avoid leaving the SDK in a loading state.
                return;
            }
            setSaveRowApiError(null);
            const linkMap = buildSavedRowLinkLookupMap(
                savedRowLinksFromApi,
                templateId,
                canvasSavedRowByRowUuidRef.current
            );
            const libraryMeta = resolveSavedRowLibraryLink(pending.row, linkMap);

            const finishSuccess = async (
                saved: { id: string; name: string; category: string },
                verb: 'saved' | 'updated'
            ) => {
                const u = getBeeRowInstanceUuid(pending.row);
                if (u && templateId != null) {
                    try {
                        await linkSavedRowToTemplateAsync({
                            templateId,
                            rowGuid: u,
                            savedRowId: saved.id,
                            name: saved.name,
                            category: saved.category,
                        });
                    } catch {
                        // Row is persisted; linkage is best-effort for reopen-as-update UX.
                    }
                } else if (u && templateId == null) {
                    canvasSavedRowByRowUuidRef.current.set(u, {
                        savedRowId: saved.id,
                        name: saved.name,
                        category: saved.category,
                    });
                }
                pending.resolve(buildSavedRowResolvePayload(pending.row, saved));
                pendingSaveRowRef.current = null;
                setSaveRowDialogOpen(false);
                setSaveRowSuccess({
                    key: Date.now(),
                    message: `Row ${saved.name} ${verb === 'saved' ? 'saved' : 'updated'} successfully`,
                });
            };

            try {
                if (libraryMeta && intent === 'update') {
                    const updated = await updateSavedRowAsync({
                        id: libraryMeta.savedRowId,
                        name,
                        category,
                        row: (pending.row as unknown) as Record<string, unknown>,
                        previousCategory: libraryMeta.category,
                    });
                    await finishSuccess(updated, 'updated');
                } else if ((libraryMeta && intent === 'saveAsNew') || (!libraryMeta && intent === 'create')) {
                    const created = await createSavedRowAsync({
                        name,
                        category,
                        row: (pending.row as unknown) as Record<string, unknown>,
                    });
                    await finishSuccess(created, 'saved');
                } else {
                    pending.reject();
                    pendingSaveRowRef.current = null;
                    setSaveRowDialogOpen(false);
                }
            } catch (e) {
                const message = e instanceof Error && e.message.trim().length > 0 ? e.message : 'Something went wrong';
                setSaveRowApiError({ key: Date.now(), message });
                return;
            }
        },
        [createSavedRowAsync, linkSavedRowToTemplateAsync, savedRowLinksFromApi, templateId, updateSavedRowAsync]
    );

    const saveRowDialogInitialValues = useMemo(() => {
        if (!saveRowDialogOpen || !pendingSaveRowRef.current) {
            return null;
        }
        const linkMap = buildSavedRowLinkLookupMap(
            savedRowLinksFromApi,
            templateId,
            canvasSavedRowByRowUuidRef.current
        );
        const meta = resolveSavedRowLibraryLink(pendingSaveRowRef.current.row, linkMap);
        return meta ? { name: meta.name, categorySlug: meta.category } : null;
    }, [saveRowDialogOpen, templateId, savedRowLinksFromApi]);

    const { data: distinctSavedRowCategories = [], isFetching: isSavedRowCategoriesFetching } = useQuery({
        queryKey: QueryKeys.SAVED_ROWS_CATEGORY_LIST,
        queryFn: async () => {
            const api = await getRowsApiInstanceRef.current();
            const { data } = await api.get<{ categories?: string[] }>(`${CuredApiPaths.SAVED_ROWS}/categories`);
            return Array.isArray(data?.categories) ? data.categories : [];
        },
        enabled: Boolean(reusableRows && canManageContent),
    });

    const savedRowCategoryOptions = useMemo((): SavedRowCategoryOption[] => {
        const merged = Array.from(new Set([...DEFAULT_SAVED_ROW_CATEGORY_HANDLES, ...distinctSavedRowCategories]));
        return merged.map((value) => ({
            value,
            label: labelForSavedRowCategoryHandle(value),
        }));
    }, [distinctSavedRowCategories]);

    const [isExportButtonPressed, setIsExportButtonPressed] = useState(false);
    const [isExportSuccess, setIsExportSuccess] = useState(false);
    const [exportError, setExportError] = useState<Error | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    const { refetch: refetchHTML } = useTemplateHTML({
        templateId: Number(id),
        reactQueryOptions: {
            enabled: isExportButtonPressed && !!id && id !== 'default',
            staleTime: 0,
            onSuccess: (html: string) => {
                downloadFile({
                    file: html,
                    fileType: 'html',
                    fileName: name,
                });
                setIsExportSuccess(true);
                setIsExporting(false);
                setIsExportButtonPressed(false);
            },
            onError: (err: Error) => {
                setExportError(err);
                setIsExporting(false);
                setIsExportButtonPressed(false);
            },
        },
    });

    const isExportLoading = isExporting;

    const handleExportClick = () => {
        setIsExporting(true);
        setExportError(null);
        if (isExportButtonPressed) {
            refetchHTML();
        } else {
            setIsExportButtonPressed(true);
        }
    };

    const [isExportPdfButtonPressed, setIsExportPdfButtonPressed] = useState(false);
    const [isExportPdfSuccess, setIsExportPdfSuccess] = useState(false);
    const [exportPdfError, setExportPdfError] = useState<Error | null>(null);
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    const { refetch: refetchPDF } = useTemplatePDF({
        templateId: Number(id),
        reactQueryOptions: {
            enabled: isExportPdfButtonPressed && !!id && id !== 'default',
            staleTime: 0,
            onSuccess: (pdf: Blob) => {
                downloadBlob({
                    blob: pdf,
                    fileName: `${name}.pdf`,
                });
                setIsExportPdfSuccess(true);
                setIsExportingPdf(false);
                setIsExportPdfButtonPressed(false);
            },
            onError: (err: Error) => {
                setExportPdfError(err);
                setIsExportingPdf(false);
                setIsExportPdfButtonPressed(false);
            },
        },
    });

    const isExportPdfLoading = isExportingPdf;

    const handleExportPdfClick = () => {
        setIsExportingPdf(true);
        setExportPdfError(null);
        if (isExportPdfButtonPressed) {
            refetchPDF();
        } else {
            setIsExportPdfButtonPressed(true);
        }
    };

    // The primary beeJson column must always hold the en-US content for backward compat.
    // - When the active tab IS en-US: beeJson is the live editor state — use it directly.
    //   (beeJsonByLanguage['en-US'] is a snapshot from the last time we *left* en-US,
    //    so it would be stale if the user has made edits since returning.)
    // - When the active tab is NOT en-US: use the stored en-US snapshot from the map,
    //   falling back to beeJson only if the map entry is missing somehow.
    const mainBeeJsonForSave = isMlEnabled
        ? selectedLanguage === DEFAULT_LANGUAGE_CODE
            ? beeJson
            : beeJsonByLanguage[DEFAULT_LANGUAGE_CODE] || beeJson
        : undefined;

    // Build multilingual bee json map for save: include the live beeJson for the current language
    const beeMlJsonForSave = (() => {
        if (isMlEnabled) {
            const allLangJsons = { ...beeJsonByLanguage, [selectedLanguage]: beeJson };
            // en-US must always be present. If it's still missing (e.g. editor hasn't initialized),
            // use mainBeeJsonForSave which already encodes the correct en-US fallback logic.
            if (!allLangJsons[DEFAULT_LANGUAGE_CODE] && mainBeeJsonForSave) {
                allLangJsons[DEFAULT_LANGUAGE_CODE] = mainBeeJsonForSave;
            }
            const result: Record<string, any> = {};
            for (const [lang, json] of Object.entries(allLangJsons)) {
                if (json) {
                    try {
                        result[lang] = JSON.parse(json);
                    } catch {
                        // skip unparseable entries
                    }
                }
            }
            return Object.keys(result).length ? result : undefined;
        }
        // ML flag is off, but preserve any non–en-US variants already stored on the template
        // (e.g. from an Inconnect HTML import).  Sending them here ensures that publishing
        // the template doesn't silently overwrite the DB's bee_ml_json / html_json with
        // just en-US — which would lose the Spanish (or other) variant entirely.
        const savedBeeMlJson = isTemplate ? (templateQuery as any)?.beeMlJson : undefined;
        if (savedBeeMlJson && Object.keys(savedBeeMlJson).length > 1 && beeJson) {
            try {
                return { ...savedBeeMlJson, [DEFAULT_LANGUAGE_CODE]: JSON.parse(beeJson) };
            } catch {
                // beeJson not yet parseable — fall through
            }
        }
        return undefined;
    })();

    const { trackChange, resetChanges } = useBeeAutoSave({
        applicationsAccess,
        beeJson,
        communicationType,
        content: templateQuery,
        id,
        isNameTooShort,
        isSaveClicked,
        isSaveEnabled: canManageContent,
        isTemplate: true,
        isTemplateLayout: isLayout,
        isUpdate,
        name,
        onSave: (input) => saveTemplate(input as CreateTemplateInput),
        onUpdate: (input) => updateTemplate(input as UpdateTemplateInput),
        parentFolderId,
        status,
        beeMlJson: beeMlJsonForSave,
        mainBeeJson: mainBeeJsonForSave,
    });

    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();
    const hasDynamicContent = isFeatureSettingsSuccess && featureSettings?.dynamic_content_enabled;
    const { data: beeConfig, isSuccess: isBeeConfigSuccess } = useBeeConfig(BeeConfigType.EMAIL);

    const mergedBeeConfig: BeeEmailEditorConfig | undefined = useMemo(() => {
        if (!isBeeConfigSuccess || !beeConfig) {
            return undefined;
        }
        const baseEmail = beeConfig as BeeEmailEditorConfig;
        const withEmptyRowGuides: BeeEmailEditorConfig = {
            ...baseEmail,
            rowsConfiguration: {
                ...(baseEmail.rowsConfiguration ?? {}),
                emptyRows: true,
            },
        };
        if (!reusableRows || !canManageContent) {
            // Explicitly disable — Beefree's SDK shows its own default "Save row" affordance (which posts to
            // Beefree's hosted Content Services API, not our self-hosted saved-rows backend, and hangs
            // indefinitely) whenever `saveRows`/`contentDialog` are simply omitted rather than turned off.
            return { ...withEmptyRowGuides, saveRows: false, contentDialog: undefined };
        }

        const extraCategoryHandles = Array.from(new Set(savedRowCategoryOptions.map((o) => o.value)));

        return buildEmailBeeSavedRowsConfig({
            base: withEmptyRowGuides,
            fetchRowsForCategory,
            promptSaveRow,
            extraCategoryHandles,
        });
    }, [
        beeConfig,
        isBeeConfigSuccess,
        reusableRows,
        canManageContent,
        fetchRowsForCategory,
        promptSaveRow,
        savedRowCategoryOptions,
    ]);
    const { data: rowDisplayConditions, isSuccess: isRowDisplayConditionsSuccess } = useRowDisplayConditions({
        enabled: !!hasDynamicContent,
    });

    // Extract display condition IDs from beeJson
    const displayConditionIds = extractDisplayConditions(templateBeeJson);

    // Filter rowDisplayConditions to only include conditions that are actually used in the template
    const usedDisplayConditions =
        isRowDisplayConditionsSuccess && rowDisplayConditions
            ? rowDisplayConditions.filter((condition) => displayConditionIds.includes(condition.id))
            : [];

    // Prepare BeeEditor props for rowDisplayConditions
    const rowDisplayConditionsProps =
        isRowDisplayConditionsSuccess && rowDisplayConditions && rowDisplayConditions.length > 0
            ? { rowDisplayConditions }
            : {};

    // Update the template name local state when the template loads.
    useEffect(() => {
        if (isTemplate && isCopy) {
            setName(`Copy of ${templateQuery.name}`);
            setStatus(ContentStatus.DRAFT); // Copies are always drafts
        } else if (isTemplate && !isCopy) {
            setName(templateQuery.name);
            setCommunicationType(templateQuery.communicationType as CommunicationType);
            setApplicationsAccess(templateQuery.applicationsAccess?.split(',') as ApplicationAccessType[]);
            // Prioritize URL status (set when converting published to draft) over API response
            // This handles the race condition where cached data might be stale
            setStatus(statusFromUrl || templateQuery.status || ContentStatus.DRAFT);
        } else if (!isNoRestart) {
            setName('Untitled');
            setStatus(ContentStatus.DRAFT); // New templates are always drafts
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTemplateSuccess]);

    // Hydrate multilingual map from saved beeMlJson when editing an existing template (flag-gated)
    useEffect(() => {
        if (!isMlEnabled || !isTemplate || !templateQuery.beeMlJson) return;
        const savedLangs = Object.keys(templateQuery.beeMlJson);
        if (!savedLangs.length) return;
        setLanguages(savedLangs);
        const links = savedRowLinksFromApi;
        const byLang: Record<string, string> = {};
        for (const lang of savedLangs) {
            let doc: unknown = templateQuery.beeMlJson[lang];
            if (links?.length) {
                doc = mergeTemplateSavedRowLinksIntoBeeJson(doc, links);
            }
            byLang[lang] = JSON.stringify(doc);
        }
        // en-US must always be present — recover from the primary beeJson column if it was dropped
        if (!byLang[DEFAULT_LANGUAGE_CODE] && templateQuery.beeJson) {
            let en: unknown = JSON.parse(templateQuery.beeJson);
            if (links?.length) {
                en = mergeTemplateSavedRowLinksIntoBeeJson(en, links);
            }
            byLang[DEFAULT_LANGUAGE_CODE] = JSON.stringify(en);
        }
        setBeeJsonByLanguage(byLang);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMlEnabled, isTemplateSuccess, savedRowLinksFromApi]);

    const handleSelectLanguage = useCallback(
        (lang: string) => {
            // Flush current editor state into the map for the language we're leaving.
            // Skip the flush if beeJson is empty (editor not yet initialized) to avoid overwriting
            // a valid stored value with an empty string, which would lose en-US content.
            const newMap = beeJson ? { ...beeJsonByLanguage, [selectedLanguage]: beeJson } : { ...beeJsonByLanguage };
            setBeeJsonByLanguage(newMap);
            setSelectedLanguage(lang);
            const storedJson = newMap[lang] || beeJson;
            setBeeJson(storedJson);
            // Load the new language's content directly into the running Bee editor instance
            setContentToLoad(JSON.parse(storedJson));
            // Clear dirty flag so any onChange from the load doesn't trigger a spurious save
            resetChanges();
        },
        [selectedLanguage, beeJson, beeJsonByLanguage, resetChanges]
    );

    const handleAddLanguage = useCallback(
        (lang: string) => {
            setLanguages((prev) => [...prev, lang]);
            // Seed the new language with the current beeJson so:
            // 1. The editor loads an explicit override (never templateBeeJson fallback)
            // 2. The user starts from the en-US template as a translation baseline
            const seedJson = beeJson || JSON.stringify(templateBeeJson);
            setBeeJsonByLanguage((prev) => {
                const next = { ...prev, [lang]: seedJson };
                // If currently on en-US and the map doesn't have it yet, capture it now.
                // This guarantees en-US is in the map before the user ever switches away.
                if (selectedLanguage === DEFAULT_LANGUAGE_CODE && beeJson && !next[DEFAULT_LANGUAGE_CODE]) {
                    next[DEFAULT_LANGUAGE_CODE] = beeJson;
                }
                return next;
            });
        },
        [beeJson, templateBeeJson, selectedLanguage]
    );

    const handleDeleteLanguage = useCallback(
        (lang: string) => {
            setLanguages((prev) => prev.filter((l) => l !== lang));
            setBeeJsonByLanguage((prev) => {
                const next = { ...prev };
                delete next[lang];
                return next;
            });
            // If currently viewing the deleted language, switch back to en-US
            if (selectedLanguage === lang) {
                const enUsJson = beeJsonByLanguage[DEFAULT_LANGUAGE_CODE] || beeJson;
                setSelectedLanguage(DEFAULT_LANGUAGE_CODE);
                setBeeJson(enUsJson);
                setContentToLoad(JSON.parse(enUsJson));
                resetChanges();
            }
        },
        [selectedLanguage, beeJsonByLanguage, beeJson, resetChanges]
    );

    /**
     * Handles the autosave event. We are not using Bee's autosave callback because the only variable we have access to is the json
     * and we need to check to see if there are unsaved changes and make the post with the name, and those variables are stale when the callback is invoked.
     */
    const handleAutoSave = (beeJson: string) => {
        setBeeJson(beeJson);
    };

    /**
     * Handles BeeJSON changes from communication type changes (removing unsubscribe links)
     * This updates the visual editor immediately but does NOT save - saving only happens on "Save & Exit"
     */
    const handleBeeChangeFromCommunicationType = (beeJson: string, beeEditorRef: any) => {
        setBeeJson(beeJson);

        // Directly update the Bee Editor content without restarting
        if (beeEditorRef && beeEditorRef.current) {
            beeEditorRef.current.load(JSON.parse(beeJson));
        }
    };

    const handleSaveBeeJson = (beeJson: string) => {
        setBeeJson(beeJson);
        setIsSaveBeeJsonClicked(true);
    };

    const handleStatusChange = (newStatus: ContentStatus) => {
        setStatus(newStatus);
    };

    const handleChangeBeeEditor = (beeJson: string, response: object) => {
        setBeeJson(beeJson);
        trackChange(response);
    };

    const handleChangeName = (e: any) => {
        if (!isNameTouched) {
            setIsNameTouched(true);
        }
        setName(e.target.value);
    };

    const handleCommunicationTypeChange = (e: any) => {
        setCommunicationType(e.target.value);
    };

    const handleApplicationsAccessChange = (e: any) => {
        setApplicationsAccess(e.target.value);
    };

    const handleSaveName = () => {
        const getJson = () => {
            if (beeJson) {
                return beeJson;
            }
            if (isTemplate) {
                return templateQuery.beeJson;
            }
            return JSON.stringify(templateQuery);
        };
        setBeeJson(getJson());
        setIsSaveNameClicked(true);
    };

    const handleClickCopy = () => {
        setName(`Copy of ${name}`);
        // Pass the isCopy flag. If the flag isn't passed, the value for isCopy defaults to false.
        history.push(`${URLPaths.CONTENT_CREATE_EMAIL}?id=${id}&isCopy=true`);
    };

    const handleCloseTestSendDialog = () => setIsTestSendDialogOpen(false);

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="template-name-input"
                    disabled={isLayout}
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={getNameHelperText(isNameUndefinedOnTouch, isNameTooShort)}
                    name={name}
                    withClickAway
                    onChange={handleChangeName}
                    onClickAway={handleSaveName}
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                id && id !== 'default' && !isCopy
                                    ? status || (templateQuery as any)?.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            <BeeEditorTestSendDialog
                handleClose={handleCloseTestSendDialog}
                isOpen={isTestSendDialogOpen}
                templateId={Number(id)}
                displayConditions={usedDisplayConditions}
                hasDynamicContent={!!hasDynamicContent}
            />
            {(isError || isNameUndefinedOnSave) && (
                <ErrorSnackbar
                    errorMessage={
                        templateError?.message ||
                        saveError?.message ||
                        updateError?.message ||
                        'Your email must include a name.'
                    }
                />
            )}
            <EmailExportSnackbar
                isDownloading={isExportLoading}
                isSuccess={isExportSuccess}
                error={exportError}
                emailName={name}
                onSuccessClosed={() => setIsExportSuccess(false)}
                onErrorClosed={() => setExportError(null)}
            />
            <EmailExportSnackbar
                isDownloading={isExportPdfLoading}
                isSuccess={isExportPdfSuccess}
                error={exportPdfError}
                emailName={name}
                onSuccessClosed={() => setIsExportPdfSuccess(false)}
                onErrorClosed={() => setExportPdfError(null)}
            />
            {saveRowSuccess && <SuccessSnackbar key={saveRowSuccess.key} successMessage={saveRowSuccess.message} />}
            {saveRowApiError && <ErrorSnackbar key={saveRowApiError.key} errorMessage={saveRowApiError.message} />}
            {isLayout && (
                <Box display="flex" flex={1} mb={1} justifyContent="center">
                    <Alert
                        elevation={0}
                        severity="warning"
                        variant="standard"
                        action={
                            <Button data-testid="copy-button" sx={buttonCopy} onClick={handleClickCopy}>
                                Copy
                            </Button>
                        }
                    >
                        Edits to this email will not be saved. Please make a copy of this email to save changes.
                    </Alert>
                </Box>
            )}
            {isMlEnabled && (
                <LanguageTabBar
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onSelectLanguage={handleSelectLanguage}
                    onAddLanguage={handleAddLanguage}
                    onDeleteLanguage={handleDeleteLanguage}
                    disabled={isEditorInPreview}
                />
            )}
            <Stack height="100%">
                {isBeeConfigSuccess && mergedBeeConfig && (
                    <BeeEditor
                        applicationsAccess={applicationsAccess}
                        beeConfig={mergedBeeConfig}
                        beeJson={templateBeeJson}
                        communicationType={communicationType}
                        handleApplicationsAccess={handleApplicationsAccessChange}
                        handleCommunicationType={handleCommunicationTypeChange}
                        isNoRestart={isNoRestart}
                        isSuccess={isTemplateSuccess}
                        isSaving={isSaving}
                        isExportLoading={isExportLoading}
                        onExportClick={handleExportClick}
                        isExportPdfLoading={isExportPdfLoading}
                        onExportPdfClick={handleExportPdfClick}
                        onAutoSave={handleAutoSave}
                        onChange={handleChangeBeeEditor}
                        onSave={handleSaveBeeJson}
                        onStatusChange={handleStatusChange}
                        openSendTestDialog={() => setIsTestSendDialogOpen(true)}
                        setIsNoRestart={setIsNoRestart}
                        template={templateQuery as Template}
                        hasDynamicContent={!!hasDynamicContent}
                        hasUsedDisplayConditions={usedDisplayConditions.length > 0}
                        onBeeChangeFromCommunicationType={handleBeeChangeFromCommunicationType}
                        contentToLoad={contentToLoad}
                        onPreviewModeChange={setIsEditorInPreview}
                        {...rowDisplayConditionsProps}
                    />
                )}
            </Stack>
            {!!hasAiBrandTools && (
                <>
                    <CopyGeneratorChat
                        open={isCopyGeneratorChatOpen}
                        onClose={() => setIsCopyGeneratorChatOpen(false)}
                        templateId={templateId}
                    />
                    <Tooltip placement="right" title="Ask Sara">
                        <Fab
                            aria-expanded={isCopyGeneratorChatOpen}
                            aria-haspopup="dialog"
                            aria-label="Ask Sara"
                            data-testid="copy-generator-chat-fab"
                            size="small"
                            sx={(theme) => ({
                                position: 'fixed',
                                left: theme.spacing(10),
                                bottom: theme.spacing(2),
                                zIndex: 1300,
                                bgcolor: 'transparent',
                                backgroundImage: 'none',
                            })}
                            onClick={() => setIsCopyGeneratorChatOpen((open) => !open)}
                        >
                            <SaraCircleMark size={40} />
                        </Fab>
                    </Tooltip>
                </>
            )}
            <SaveRowDialog
                open={saveRowDialogOpen}
                initialValues={saveRowDialogInitialValues}
                isSaving={isSavingSavedRowMutation}
                categoriesLoading={isSavedRowCategoriesFetching}
                categoryOptions={savedRowCategoryOptions}
                onClose={handleSavedRowDialogClose}
                onSubmit={handleSavedRowDialogSubmit}
            />
        </Stack>
    );
};

export default ContentEmailBuilderPage;
